<template>
  <div class="advanced-view">
    <div class="panels" :class="{ 'selection-active': hasSolutions }">
      <div class="scroll-window">
        <vue-perfect-scrollbar
          class="scroll-area left"
          id="module-scroll"
          ref="moduleScroll"
          @ps-scroll-y="setModuleGraphSticky()"
          @ps-y-reach-start="unsetModuleGraphSticky()"
        >
          <easy-minimize
            class="module"
            ref="modulePanel"
            :title-heading="'Modules'"
            :total="modules.length"
            :title-minimized="'Modules'"
            :direction="'left'"
            :minimized="minimizeModuleInput"
            @minimize="onModuleShowChange(true)"
            @maximize="onMaximize('module')"
          >
            <accordion>
              <accordion-item
                :title="'Graph'"
                :openOnLoad="false"
                id="module-graph"
                ref="moduleGraph"
                @toggleContent="updateScrollbar"
              >
                <div class="no-graph-notice" v-if="!activeModule">
                  Select a module to view the graph.
                </div>
                <div v-else class="accordion-media">
                  <img
                    class="mr-2"
                    :src="this.$imageBase + activeModule.image"
                    :alt="activeModule.name"
                  />
                  <div class="accordion-media-body">
                    <span class="font-weight-bold">{{
                      activeModule.name
                    }}</span>
                  </div>
                </div>
                <ModuleLegend
                  :module="activeModule"
                  class="legend-padding"
                  v-if="activeModule"
                >
                  <easy-charts
                    type="module"
                    :module="module"
                    :region="region"
                  />
                </ModuleLegend>
              </accordion-item>
              <div
                id="module-graph-placeholder"
                class="module-graph-placeholder"
                ref="moduleGraphPlaceholder"
              ></div>
              <accordion-item
                :title="calculateAccordionTitle"
                id="module-calculate-performance"
                :openOnLoad="true"
                @toggleContent="updateScrollbar"
              >
                <ModuleCalculationForm />
              </accordion-item>
              <accordion-item
                v-if="!multiLength"
                :threeDot="true"
                :threeDotItems="[
                  { id: 'resetModules', displayText: 'Reset' },
                  { id: 'load', displayText: 'Load' },
                  { id: 'save', displayText: 'Save' },
                ]"
                :additionalActionItems="[
                  {
                    id: 'showAllActiveModuleFilters',
                    displayText: 'All active filters',
                  },
                ]"
                :title="'Filters'"
                :btnText="'Clear filters'"
                id="module-filters"
                @clearFiltersClicked="onClearModuleFiltersClicked"
                @threeDotItemClicked="threeDotItemClicked"
                @additionalActionItemClicked="additionalActionItemClicked"
                :showAdditionalItems="hasActiveModuleFilters"
                @toggleContent="updateScrollbar"
                :eventBus="EventBus"
                openEvent="openModuleFiltersAccordion"
              >
                <div class="search-container">
                  <text-field
                    placeholder="Filter on part of name, 12NC, etc."
                    @keypress.enter="onSearchInputChange($event, 'module')"
                    :value="moduleSearchTerm"
                  />
                </div>
                <global-feature-filters />
                <easy-filters
                  @change="onModuleFilterChange"
                  @clear="onClear('modules', ...arguments)"
                  :distanceUnit="distanceUnit"
                  :filters="moduleCombinationsFilter"
                />
                <div class="filters">
                  <easy-filters-group
                    @change="onModuleFilterChange"
                    @clear="onClear('modules', ...arguments)"
                    :distanceUnit="distanceUnit"
                    :filters="moduleFiltersToShow.filtersGroup"
                  />
                </div>
                <easy-filters
                  @change="onModuleFilterChange"
                  @clear="onClear('modules', ...arguments)"
                  :distanceUnit="distanceUnit"
                  :filters="moduleFiltersToShow.filters"
                  @toggleFilter="updateScrollbar"
                  :eventBus="EventBus"
                  openEvent="openModuleFilters"
                />
              </accordion-item>
              <accordion-item
                v-if="canI('MULTI_LENGTH') && canMultiLength"
                :title="'Module length selector'"
                :openOnLoad="false"
                id="multilength"
                @toggleContent="updateScrollbar"
              >
                <div class="multi-length-accordion">
                  <p>
                    Create a multi length system containing selected Module.
                  </p>
                  <button
                    class="btn btn-secondary btn-block"
                    v-modal-link="{
                      name: 'guide',
                      props: {
                        show: true,
                        multiLength: true,
                        withModule: true,
                      },
                    }"
                  >
                    Start
                  </button>
                </div>
              </accordion-item>
            </accordion>
          </easy-minimize>
        </vue-perfect-scrollbar>
      </div>
      <splitpanes watch-slots class="default-theme" @resized="onPaneResized">
        <div
          class="results-block module"
          :splitpanes-size="paneSize"
          :class="{ 'hide-module-cta': paneSize < 10 }"
        >
          <div class="module-table">
            <easy-table-options-bar
              :options="moduleTableOptions"
              :activeOption="moduleActiveTableOptionTab"
              :columnsVisibilty="resultModuleColumnSet"
              :pinnedHeaders="pinnedModuleHeaders"
              :pinnedItems="pinnedModules"
              :multiLengthView="multiLength"
              :comparedPreviewColumnDefs="compareModuleTableColumnDefs"
              :comparedItems="comparedModules"
              @setActive="moduleOptionBarAction"
              @columnsVisibilityChange="
                onColumnVisibilityChange(
                  $event,
                  multiLength ? 'multi-length' : 'module'
                )
              "
              @unpin="unpinModule"
              @addToSolution="addPinnedModuleToSolution"
              @compare="onCompareModules"
              @removeComparedItem="onRemoveComparedModule"
              @clearComparedSelection="onClearComparedModules"
            />
            <easy-table
              ref="moduleTable"
              id="module-table"
              :columnDefs="moduleColumnDefsToShow"
              :defaultColDefs="{ resizable: true }"
              :headerHeight="40"
              :rowHeight="32"
              :rowData="modules"
              :visibleColumns="resultModuleColumnSet"
              :eventBus="EventBus"
              :multiLengthView="multiLength"
              :activeProduct="activeModule"
              :hasActiveSolution="!!solution"
              @ctaAddToSolution="
                onComponentAddToSolution($event.data, 'module')
              "
              @ctaAddToNewSolution="
                onComponentAddToNewSolution($event.data, 'module')
              "
              @ctaMoreInfo="onComponentMoreInfo($event, 'module')"
              @ctaAddToComparison="onComponentAddToComparison($event, 'module')"
              @ctaCopyToClipboard="onComponentCopyToClipboard($event, 'module')"
              @ctaCopyLink="onComponentCopyLink($event, 'module')"
              @selectRow="onSelectModuleRow"
            />
          </div>
        </div>
        <div
          class="results-block driver"
          :splitpanes-size="100 - paneSize"
          :class="{ 'hide-driver-cta': paneSize > 90 }"
        >
          <div class="driver-table">
            <easy-table-options-bar
              :options="driverTableOptions"
              :activeOption="driverActiveTableOptionTab"
              :columnsVisibilty="resultDriverColumnSet"
              :pinnedHeaders="pinnedDriverHeaders"
              :pinnedItems="pinnedDrivers"
              :comparedPreviewColumnDefs="compareDriverTableColumnDefs"
              :comparedItems="comparedDrivers"
              @setActive="driverOptionBarAction"
              @columnsVisibilityChange="
                onColumnVisibilityChange($event, 'driver')
              "
              @unpin="unpinDriver"
              @addToSolution="addPinnedDriverToSolution"
              @compare="onCompareDrivers"
              @removeComparedItem="onRemoveComparedDriver"
              @clearComparedSelection="onClearComparedDrivers"
            />
            <easy-table
              ref="driverTable"
              id="driver-table"
              :columnDefs="driverColumnDefs"
              :defaultColDefs="{ resizable: true }"
              :headerHeight="40"
              :rowHeight="32"
              :rowData="drivers"
              :visibleColumns="resultDriverColumnSet"
              :eventBus="EventBus"
              :activeProduct="driver"
              :hasActiveSolution="!!solution"
              @ctaAddToSolution="
                onComponentAddToSolution($event.data, 'driver')
              "
              @ctaAddToNewSolution="
                onComponentAddToNewSolution($event.data, 'driver')
              "
              @ctaMoreInfo="onComponentMoreInfo($event, 'driver')"
              @ctaAddToComparison="onComponentAddToComparison($event, 'driver')"
              @ctaCopyToClipboard="onComponentCopyToClipboard($event, 'driver')"
              @ctaCopyLink="onComponentCopyLink($event, 'driver')"
              @selectRow="onSelectDriverRow"
              @newColumnsLoaded="resizeColumns"
            />
          </div>
        </div>
      </splitpanes>
      <div class="scroll-window">
        <vue-perfect-scrollbar
          class="scroll-area right"
          :style="{ height: driverPanelHeight }"
          ref="driverScroll"
          @ps-scroll-y="setDriverGraphSticky()"
          @ps-y-reach-start="unsetDriverGraphSticky()"
        >
          <easy-minimize
            ref="driverPanel"
            class="driver"
            :title-heading="'Drivers'"
            :title-minimized="'Drivers'"
            :total="drivers.length"
            :direction="'right'"
            :minimized="minimizeDriverInput"
            @minimize="onDriverShowChange(true)"
            @maximize="onMaximize('driver')"
          >
            <accordion>
              <accordion-item
                :title="'Graph'"
                :openOnLoad="false"
                id="driver-graph"
                ref="driverGraph"
                @toggleContent="updateScrollbar"
              >
                <div class="no-graph-notice" v-if="!driver">
                  Select a driver to view the graph.
                </div>
                <div v-else class="accordion-media">
                  <img
                    class="mr-2"
                    :src="this.$imageBase + driver.image"
                    :alt="driver.marketingName"
                  />
                  <div class="accordion-media-body">
                    <span class="font-weight-bold">{{
                      driver.marketingName
                    }}</span>
                  </div>
                </div>
                <DriverLegend class="legend-padding" v-if="driver">
                  <easy-charts
                    v-if="driver"
                    type="driver"
                    :driver="driver"
                    :module="solutionModule"
                    :region="region"
                    :openOnLoad="true"
                  />
                </DriverLegend>
              </accordion-item>
              <div
                id="driver-graph-placeholder"
                class="driver-graph-placeholder"
                ref="driverGraphPlaceholder"
              ></div>
              <accordion-item
                :title="'Calculate performance'"
                :openOnLoad="true"
                @toggleContent="updateScrollbar"
              >
                <DriverCalculationForm />
              </accordion-item>
              <accordion-item
                :threeDot="true"
                :threeDotItems="[
                  { id: 'resetDrivers', displayText: 'Reset' },
                  { id: 'load', displayText: 'Load' },
                  { id: 'save', displayText: 'Save' },
                ]"
                :additionalActionItems="[
                  {
                    id: 'showAllActiveDriverFilters',
                    displayText: 'All active filters',
                  },
                ]"
                :title="'Filters'"
                :btnText="'Clear filters'"
                @clearFiltersClicked="onClearDriverFiltersClicked"
                @threeDotItemClicked="threeDotItemClicked"
                @additionalActionItemClicked="additionalActionItemClicked"
                :showAdditionalItems="hasActiveDriverFilters"
                @toggleContent="updateScrollbar"
              >
                <div class="search-container">
                  <text-field
                    placeholder="Filter on part of name, 12NC, etc."
                    @keypress.enter="onSearchInputChange($event, 'driver')"
                    :value="driverSearchTerm"
                  />
                </div>
                <global-feature-filters />
                <div class="filters">
                  <easy-filters-group
                    @change="onDriverFilterChange"
                    @clear="onClear('drivers', ...arguments)"
                    :distanceUnit="distanceUnit"
                    :filters="driverFiltersToShow.filtersGroup"
                  />
                </div>
                <easy-filters
                  @change="onDriverFilterChange"
                  @clear="onClear('drivers', ...arguments)"
                  :filters="driverFiltersToShow.filters"
                  :distanceUnit="distanceUnit"
                  @toggleFilter="updateScrollbar"
                />
              </accordion-item>
            </accordion>
          </easy-minimize>
        </vue-perfect-scrollbar>
      </div>
    </div>
    <easy-selection
      class="solution"
      :solutions="solutions"
      :active="active"
      :region="region"
      @removeModule="onRemoveModule"
      @removeDriver="onRemoveDriver"
      @removeSensor="onRemoveSensor"
      @removeBridge="onRemoveBridge"
      @selectSolution="onSelectSolution"
      @viewSolution="onViewSolution"
      @addNewSolution="addNewSolution"
      @removeSolution="removeSolution"
      @removeEmergencyDriver="onRemoveEmergencyDriver"
      @clickModule="onClickModule"
      @clickDriver="onClickDriver"
    />
    <dialog id="modal">
      <main class="modal-main">
        <h2>Item not found</h2>
        <div>
          The 12nc was not found
          <p>12nc code: {{ searchItem.nc12 }}</p>
        </div>
        <div>
          <button
            class="btn btn-secondary btn-block btn-sm"
            @click="
              () => {
                this.getModal.close();
              }
            "
          >
            close
          </button>
        </div>
      </main>
    </dialog>
  </div>
</template>

<script>
import { cloneDeep } from "lodash";
import { mapGetters, mapState } from "vuex";
import { EventBus } from "@/eventbus.js";
import Tour from "driver.js";
import { tourSteps, tourConfig } from "@/tour";
import Splitpanes from "splitpanes";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import ModuleCalculationForm from "@/components/ModuleCalculationForm";
import ComponentDetailModal from "@/components/modals/ComponentDetailModal";
import SolutionModal from "@/components/modals/SolutionModal";
import FilterListModal from "@/components/modals/FilterListModal";
import DriverCalculationForm from "@/components/DriverCalculationForm";
import GlobalFeatureFilters from "@/components/filters/GlobalFeatureFilters";
import ModuleLegend from "@/components/legends/ModuleLegend";
import DriverLegend from "@/components/legends/DriverLegend";
import { CellRenderer } from "@/utils/ColumnMapping";
import Guide from "@/views/guide/Guide";
import SaveFiltersetModal from "@/components/modals/SaveFiltersetModal";
import OutdoorDriverNoticeModal from "@/components/modals/OutdoorDriverNoticeModal";
import ValidateErrorModal from "@/components/modals/ValidateErrorModal";
import CompareItemsModal from "@/components/modals/CompareItemsModal";
import { MAX_COMPARE_PRODUCTS, REGION_NA } from "@/service/Constants";
import { getInputVoltage } from "@/utils/column_mapping/DriverColumnMapping";

export default {
  name: "AdvancedView",
  components: {
    DriverLegend,
    ModuleLegend,
    Splitpanes,
    ModuleCalculationForm,
    DriverCalculationForm,
    VuePerfectScrollbar,
    GlobalFeatureFilters,
  },
  modals: {
    ComponentDetailModal,
    SolutionModal,
    FilterListModal,
    Guide,
    SaveFiltersetModal,
    OutdoorDriverNoticeModal,
    ValidateErrorModal,
    CompareItemsModal,
  },
  data() {
    return {
      tour: [],
      modules: [],
      drivers: [],
      pinnedModuleHeaders: [
        { name: "Name", key: "name" },
        {
          name: "Datasheet",
          key: "datasheetUrl",
          valueFormatter: (value) => {
            return `<a href="${value}" target="_blank">View</a>`;
          },
        },
        { name: "Application", key: "familyLevelOne" },
        {
          name: "Flux (lm)",
          key: "calculationResult.outputFluxOfArray",
          valueFormatter: (value) => {
            return Math.round(value);
          },
        },
      ],
      pinnedDriverHeaders: [
        { name: "Name", key: "name" },
        {
          name: "Datasheet",
          key: "datasheetUrl",
          valueFormatter: (value) => {
            return `<a href="${value}" target="_blank">View</a>`;
          },
        },
        { name: "Application", key: "familyLevelOne" },
        { name: "P out (max W)", key: "opMax" },
      ],
      modulePanelHeight: "auto",
      driverPanelHeight: "auto",
      moduleActiveTableOptionTab: null,
      driverActiveTableOptionTab: null,
      unwatchBaseLayoutMounted: null,
      EventBus,
      moduleGraphTop: 0,
      showArchivedFilter: false,
    };
  },
  props: {
    fetchFiltersOnInit: {
      type: Boolean,
      default: true,
    },
    fetchViewOnInit: {
      type: Boolean,
      default: true,
    },
    searchItem: {
      type: Object,
      default: () => {
        return { nc12: "", type: "" };
      },
    },
    openSolutionModal: {
      type: Boolean,
      default: false,
    },
    sharedData: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters("brain/ui", [
      "minimizeModuleInput",
      "minimizeDriverInput",
      "paneSize",
      "showTour",
      "baseLayoutMounted",
    ]),

    ...mapGetters("brain/ui", {
      standardModuleColumnVisibility: "standardModuleColumnVisibility",
      standardDriverColumnVisibility: "standardDriverColumnVisibility",
      standardMultiLengthColumnVisibility:
        "standardMultiLengthColumnVisibility",
      savedModuleColumnVisibility: "savedModuleColumnVisibility",
      savedDriverColumnVisibility: "savedDriverColumnVisibility",
      savedMultiLengthColumnVisibility: "savedMultiLengthColumnVisibility",
    }),

    ...mapGetters("brain/auth", [
      "isImpersonating",
      "isLoggedIn",
      "isAdministering",
    ]),

    ...mapState("brain", ["distanceUnit", "region"]),

    ...mapState("brain/global", {
      checkReload: "checkReload",
      globalFilters: "filters",
    }),

    ...mapGetters("brain/global", {
      showArchive: "showArchive",
    }),

    ...mapState("brain/modules", {
      module: "module",
      storeModules: "modules",
      initialModuleFilters: "initialFilters",
      emptyInitialFilters: "initialFilters",
      moduleSearchTerm: "moduleSearchTerm",
      pinnedModules: "pinnedModules",
      comparedModules: "comparedModules",
      selectedMultiLengthModule: "selectedMultiLengthModule",
      incompatibleModule: "incompatibleModule",
    }),

    ...mapState("brain/drivers", {
      initialDriverFilters: "initialFilters",
      driverFilters: "filters",
      driver: "driver",
      storeDrivers: "drivers",
      driverSearchTerm: "driverSearchTerm",
      pinnedDrivers: "pinnedDrivers",
      comparedDrivers: "comparedDrivers",
    }),

    ...mapGetters("brain/modules", {
      moduleFilters: "filters",
      initialModuleColumnDefs: "initialColumnDefs",
      moduleColumnDefs: "columnDefs",
      initialMultiLengthColumnDefs: "initialMultiLengthColumnDefs",
      multiLengthColumnDefs: "multiLengthColumnDefs",
    }),

    ...mapGetters("brain/drivers", {
      initialDriverColumnDefs: "initialColumnDefs",
      driverColumnDefs: "columnDefs",
    }),

    ...mapGetters("brain/solutions", [
      "hasSolutions",
      "solutions",
      "solution",
      "active",
      "virtualModule",
      "multiLengthModules",
      "multiLength",
    ]),

    calculateAccordionTitle() {
      return this.canI("RTH_MODULE_CALCULATIONS")
        ? "Calculate"
        : "Calculate performance";
    },

    solutionHasModule() {
      return this.hasSolutions && this.solution.module;
    },

    getModal() {
      return document.getElementById("modal");
    },

    releasedSystemsOnly() {
      return this.globalFilters.find(
        (filter) => filter.field === "whitelistingEnabled"
      ).value;
    },

    activeModule() {
      if (this.multiLength) {
        return this.selectedMultiLengthModule;
      }
      return this.module;
    },

    moduleColumnDefsToShow() {
      return this.multiLength
        ? this.multiLengthColumnDefs
        : this.moduleColumnDefs;
    },

    compareModuleTableColumnDefs() {
      return this.moduleColumnDefsToShow;
    },

    compareModuleHeaders() {
      return [
        {
          name: "",
          key: "image",
          valueFormatter: (value) =>
            `<img src="${
              this.$imageBase + value
            }" height="100px" width="100px"/>`,
        },
        {
          name: "Module name",
          key: "name",
        },
        {
          name: "12nc",
          key: "nc12",
        },
        {
          name: "Datasheet",
          key: "datasheetUrl",
          valueFormatter: (value) =>
            `<a href="${value}" target="_blank" ref="noopener noreferrer">View</a>`,
        },
        {
          name: "Application",
          key: "familyLevelOne",
        },
        {
          name: "Flux",
          key: "calculationResult.outputFluxOfArray",
          valueFormatter: (value) => `${Math.round(value)} lm`,
        },
        {
          name: "Current",
          key: "calculationResult.operatingCurrent",
          valueFormatter: (value) => `${Math.round(value * 1000)} mA`,
        },
        {
          name: "Efficacy",
          key: "calculationResult.luminousEfficacyOfModule",
          valueFormatter: (value) => `${Math.round(value)} lm/W`,
        },
        {
          name: "Tc",
          key: "calculationResult.temperature",
          valueFormatter: (value) => `${value} °C`,
        },
        {
          name: "Modules in series",
          key: "calculationResult.numberOfModulesInSeries",
        },
        {
          name: "Modules in parallel",
          key: "numberOfModulesInParallel",
        },
        {
          name: "Voltage",
          key: "calculationResult.outputVoltageOfArray",
          valueFormatter: (value) => `${Math.round(value * 10) / 10} V`,
        },
        {
          name: "Power",
          key: "calculationResult.power",
          valueFormatter: (value) => `${Math.round(value * 10) / 10} W`,
        },
        {
          name: "Thermal power",
          key: "calculationResult.thermalPower",
          valueFormatter: (value) => {
            if (value < 10) {
              return `${Math.round(value * 10) / 10} W`;
            }
            return `${Math.round(value)} W`;
          },
        },
        {
          name: "LES diameter",
          key: "housing.dia",
          valueFormatter: (value) => {
            if (value <= 0) {
              return "";
            }

            return this.region === REGION_NA
              ? `${Math.round((value / 25.4) * 100) / 100} inch`
              : `${Math.round(value)} mm`;
          },
        },
        {
          name: "Length module",
          key: "housing.length",
          valueFormatter: (value) =>
            `${Math.round(value * 10) / 10} ${
              this.region === REGION_NA ? "inch" : "mm"
            }`,
        },
        {
          name: "Width module",
          key: "housing.width",
          valueFormatter: (value) =>
            `${Math.round(value * 10) / 10} ${
              this.region === REGION_NA ? "inch" : "mm"
            }`,
        },
        {
          name: "Height module",
          key: "housing.height",
          valueFormatter: (value) =>
            `${Math.round(value * 10) / 10} ${
              this.region === REGION_NA ? "inch" : "mm"
            }`,
        },
      ];
    },

    compareDriverTableColumnDefs() {
      return this.driverColumnDefs;
    },

    compareDriverHeaders() {
      const headers = [
        {
          name: "",
          key: "image",
          valueFormatter: (value) =>
            `<img src="${
              this.$imageBase + value
            }" height="100px" width="100px"/>`,
        },
        {
          name: "Driver name",
          key: "name",
        },
        {
          name: "12nc",
          key: "nc12",
        },
        {
          name: "P out",
          key: "driverCalculationResult.outputPower",
          valueFormatter: (value) => `${value} W`,
        },
        {
          name: "I out",
          key: "driverCalculationResult.outputCurrent",
          valueFormatter: (value) => `${value * 1000} mA`,
        },
        {
          name: "V out",
          key: "driverCalculationResult.outputVoltage",
          valueFormatter: (value) => `${value} V`,
        },
        {
          name: "Datasheet",
          key: "datasheetUrl",
          valueFormatter: (value) =>
            `<a href="${value}" target="_blank" ref="noopener noreferrer">View</a>`,
        },
        {
          name: "Application",
          key: "familyLevelOne",
        },
        {
          name: "P out max",
          key: "opMax",
          valueFormatter: (value) => `${value} W`,
        },
        {
          name: "I out min",
          key: "ocMin",
          valueFormatter: (value) => `${value * 1000} mA`,
        },
        {
          name: "I out max",
          key: "ocMax",
          valueFormatter: (value) => `${value * 1000} mA`,
        },
        {
          name: "V out min",
          key: "ovMin",
          valueFormatter: (value) => `${value} V`,
        },
        {
          name: "V out max",
          key: "ovMax",
          valueFormatter: (value) => `${value} V`,
        },
        {
          name: "Set output current",
          key: "driverConfigurability",
          valueFormatter: (value) => value.join(", "),
        },
        {
          name: "Dimming",
          key: "dimmingTypes",
          valueFormatter: (value) => value.map((d) => d.name).join(", "),
        },
        {
          name: "Input voltage",
          key: "inputVoltages",
          valueFormatter: (value) => getInputVoltage(value),
          hidden: this.region !== REGION_NA,
        },
        {
          name: "Eff. at full load - (%) at max input voltage",
          key: "eff",
          valueFormatter: (value) => `${value} %`,
        },
        {
          name: "Eff. at operating point at 230Vac",
          key: "driverCalculationResult.mainsEfficiency.230",
          valueFormatter: (value) => `${Math.round(value * 10) / 10} %`,
          hidden: this.region === REGION_NA,
        },
        {
          name: "Eff. at operating point at 120Vac",
          key: "driverCalculationResult.mainsEfficiency.120",
          valueFormatter: (value) => `${Math.round(value * 10) / 10} %`,
          hidden: this.region !== REGION_NA,
        },
        {
          name: "Eff. at operating point at 277Vac",
          key: "driverCalculationResult.mainsEfficiency.277",
          valueFormatter: (value) => `${Math.round(value * 10) / 10} %`,
          hidden: this.region !== REGION_NA,
        },
        {
          name: "Eff. at operating point at 347Vac",
          key: "driverCalculationResult.mainsEfficiency.347",
          valueFormatter: (value) => `${Math.round(value * 10) / 10} %`,
          hidden: this.region !== REGION_NA,
        },
        {
          name: "Eff. at operating point at 480Vac",
          key: "driverCalculationResult.mainsEfficiency.480",
          valueFormatter: (value) => `${Math.round(value * 10) / 10} %`,
          hidden: this.region !== REGION_NA,
        },
        {
          name: "Class 2",
          key: "classification.id",
          valueFormatter: (value) => value === 2,
          hidden: this.region !== REGION_NA,
        },
        {
          name: "Certification",
          key: "certifications",
          valueFormatter: (value) => value.map((c) => c.name).join(", "),
          hidden: this.region !== REGION_NA,
        },
        {
          name: "Isolation",
          key: "isolationClass.name",
        },
        {
          name: "Mounting",
          key: "mountingTypes",
          valueFormatter: (value) => value.map((m) => m.name).join(", "),
        },
        {
          name: "Length",
          key: "housing.length",
          valueFormatter: (value) =>
            `${value} ${this.region === REGION_NA ? "inch" : "mm"}`,
        },
        {
          name: "Width",
          key: "housing.width",
          valueFormatter: (value) =>
            `${value} ${this.region === REGION_NA ? "inch" : "mm"}`,
        },
        {
          name: "Height",
          key: "housing.height",
          valueFormatter: (value) =>
            `${value} ${this.region === REGION_NA ? "inch" : "mm"}`,
        },
      ];

      return headers.filter((h) => !h.hidden);
    },

    hasActiveModuleFilters() {
      return this.hasActiveFilters(this.moduleFilters);
    },

    hasActiveDriverFilters() {
      return this.hasActiveFilters(this.driverFilters);
    },

    solutionModule() {
      if (this.solution) {
        return this.solution.module != null ? this.solution.module : null;
      }
      return null;
    },

    solutionDriver() {
      if (this.solution) {
        return this.solution.driver != null ? this.solution.driver : null;
      }
      return null;
    },

    resultModuleColumnSet() {
      let standardColumnSet = cloneDeep(this.standardModuleColumnVisibility);
      const savedColumnSet = this.savedModuleColumnVisibility;
      savedColumnSet.forEach((col) => {
        const index = standardColumnSet.findIndex(
          (standardCol) => standardCol.headerName === col.headerName
        );
        if (index !== -1) {
          standardColumnSet[index].value = col.value;
        }
      });

      return standardColumnSet;
    },

    resultMultiLengthColumnSet() {
      let standardColumnSet = cloneDeep(
        this.standardMultiLengthColumnVisibility
      );
      const savedColumnSet = this.savedMultiLengthColumnVisibility;
      savedColumnSet.forEach((col) => {
        const index = standardColumnSet.findIndex(
          (standardCol) => standardCol.headerName === col.headerName
        );
        if (index !== -1) {
          standardColumnSet[index].value = col.value;
        }
      });
      return standardColumnSet;
    },
    resultDriverColumnSet() {
      let standardColumnSet = cloneDeep(this.standardDriverColumnVisibility);
      const savedColumnSet = this.savedDriverColumnVisibility;
      savedColumnSet.forEach((col) => {
        const index = standardColumnSet.findIndex(
          (standardCol) => standardCol.headerName === col.headerName
        );
        if (index !== -1) {
          standardColumnSet[index].value = col.value;
        }
      });
      return standardColumnSet;
    },
    moduleTableOptions() {
      const options = [{ icon: "download", title: "Download" }];

      if (this.canI("TOGGLE_VISIBLE_COLUMNS")) {
        options.unshift({ icon: "columns", title: "Columns" });
      }

      if (this.canI("COMPARE")) {
        options.push({ icon: "compare", title: "Compare" });
      }

      if (this.isLoggedIn && !this.multiLength) {
        options.push({ icon: "pin", title: "Pin" });
      }

      return options;
    },
    driverTableOptions() {
      const options = [{ icon: "download", title: "Download" }];

      if (this.canI("TOGGLE_VISIBLE_COLUMNS")) {
        options.unshift({ icon: "columns", title: "Columns" });
      }

      if (this.canI("COMPARE")) {
        options.push({ icon: "compare", title: "Compare" });
      }

      if (this.isLoggedIn) {
        options.push({ icon: "pin", title: "Pin" });
      }

      return options;
    },
    availableModuleFilters() {
      return this.moduleFilters
        .filter((filter) => filter.filters[0].availableValues !== undefined)
        .filter((filter) => filter.title !== "Exceptions")
        .map((filter) => {
          if (this.showArchive || filter.title !== "Release state") {
            return filter;
          }

          filter = cloneDeep(filter);
          filter.filters[0].availableValues =
            filter.filters[0].availableValues.filter(
              (filter) => filter.value !== "Archived"
            );
          return filter;
        });
    },
    moduleFiltersToShow() {
      const orgModuleFilters = this.availableModuleFilters;

      const filters = orgModuleFilters.filter(
        (filter) =>
          filter.title !== "Application" &&
          filter.title !== "Series" &&
          filter.title !== "Family" &&
          filter.title !== "Module combinations"
      );

      const filtersGroup = orgModuleFilters.filter(
        (filter) =>
          filter.title === "Application" ||
          filter.title === "Series" ||
          filter.title === "Family"
      );

      const filtersObj = {
        filters: filters,
        filtersGroup: {
          groupTitle: "Product Family",
          groups: filtersGroup,
        },
      };
      return filtersObj;
    },
    moduleCombinationsFilter() {
      return this.availableModuleFilters.filter(
        (f) => f.title === "Module combinations"
      );
    },
    driverFiltersToShow() {
      const orgDriverFilters = this.driverFilters
        .filter((filter) => filter.filters[0].availableValues !== undefined)
        .filter((filter) => filter.title !== "Exceptions")
        .filter((filter) => filter.title !== "Single currents")
        .map((filter) => {
          if (this.showArchive || filter.title !== "Release state") {
            return filter;
          }

          filter = cloneDeep(filter);
          filter.filters[0].availableValues =
            filter.filters[0].availableValues.filter(
              (filter) => filter.value !== "Archived"
            );
          return filter;
        })
        .map((filter) => {
          if (filter.title === "V-open-load") {
            return {
              ...filter,
              distanceUnit: "Vpk",
            };
          }
          return filter;
        });

      const filters = orgDriverFilters.filter(
        (filter) =>
          filter.title !== "Application" &&
          filter.title !== "Series" &&
          filter.title !== "Family"
      );

      const filtersGroup = orgDriverFilters.filter(
        (filter) =>
          filter.title === "Application" ||
          filter.title === "Series" ||
          filter.title === "Family"
      );

      const filtersObj = {
        filters: filters,
        filtersGroup: {
          groupTitle: "Product Family",
          groups: filtersGroup,
        },
      };
      return filtersObj;
    },
    canMultiLength() {
      if (this.module) {
        return this.module.tags["3_multi_length"] !== undefined;
      }
      return false;
    },
    moduleGraphCollapse() {
      return true;
    },
  },

  methods: {
    updateScrollbar() {
      setTimeout(() => {
        this.$nextTick(() => {
          this.$refs.moduleScroll.update();
          this.$refs.driverScroll.update();
        });
        // Wait for animation to complete before updating scrollbar
      }, 250);
    },
    resizeColumns() {
      setTimeout(
        function () {
          EventBus.$emit("resizeColumns");
        }.bind(EventBus),
        200
      );
    },
    hasActiveFilters(myFilters) {
      let filtersExists = false;
      for (let filters in myFilters) {
        let filter = myFilters[filters].filters;
        for (let f in filter) {
          if (filter[f].value !== null) {
            if (typeof filter[f].value !== "undefined") {
              if (
                typeof filter[f].value === "object" &&
                filter[f].value.length > 0
              ) {
                filtersExists = true;
                break;
              } else if (
                typeof filter[f].value !== "object" &&
                filter[f].value !== ""
              ) {
                filtersExists = true;
                break;
              }
            }
          }
        }
        if (filtersExists) {
          break;
        }
      }
      return filtersExists;
    },
    threeDotItemClicked(item) {
      switch (item) {
        case "load":
          if (!this.isLoggedIn) {
            this.$snotify.error(
              "Loading filters is only available for logged in users"
            );
          } else {
            this.$router.push({ name: "savedFilters" });
          }
          break;
        case "resetModules":
          this.onClearModuleFiltersClicked();
          break;
        case "resetDrivers":
          this.onClearDriverFiltersClicked();
          break;
        case "save":
          if (!this.isLoggedIn) {
            this.$snotify.error(
              "Saving filters is only available for logged in users"
            );
          } else {
            this.$modalRouter.push({
              name: "SaveFiltersetModal",
              props: { show: true },
              model: "show",
              on: { onSaveFilters: this.onSaveFilters },
            });
          }
          break;
      }
    },
    additionalActionItemClicked(item) {
      switch (item) {
        case "showAllActiveDriverFilters":
          this.showAllTags("driver");
          break;
        case "showAllActiveModuleFilters":
          this.showAllTags("module");
          break;
        default:
          console.log("Unrecognized additional item action");
      }
    },
    getDriverColumnDefs() {
      const columnDefs = cloneDeep(this.initialDriverColumnDefs);
      const columnSet = cloneDeep(this.resultDriverColumnSet);
      const driverColumnDefs = this.getColumnDefs(
        columnDefs,
        columnSet,
        "driver"
      );

      this.$store.commit("brain/drivers/setColumnDefs", driverColumnDefs);
    },
    getModuleColumnDefs() {
      const columnDefs = cloneDeep(this.initialModuleColumnDefs);
      const columnSet = cloneDeep(this.resultModuleColumnSet);
      const moduleColumnDefs = this.getColumnDefs(
        columnDefs,
        columnSet,
        "module"
      );

      this.$store.commit("brain/modules/setColumnDefs", moduleColumnDefs);
    },
    getMultiLengthColumnDefs() {
      const columnDefs = cloneDeep(this.initialMultiLengthColumnDefs);
      const columnSet = cloneDeep(this.resultMultiLengthColumnSet);
      const multiLengthColumnDefs = this.getColumnDefs(
        columnDefs,
        columnSet,
        "multi-length"
      );

      this.$store.commit(
        "brain/modules/setMultiLengthColumnDefs",
        multiLengthColumnDefs
      );
    },
    getColumnDefs(columnDefs, columnSet, type) {
      // Todo: we should be able to do this more efficiently
      columnSet.map((column) => {
        if (!column.value) {
          const indexToHide = columnDefs.findIndex(
            (x) => x.field === column.name
          );
          columnDefs[indexToHide].hide = true;
        }
      });

      const visibleColumns = columnSet.filter((column) => column.value);
      if (visibleColumns.length) {
        const firstVisibleColumnName = visibleColumns[0].name;
        const firstColumnIndex = columnDefs.findIndex((column) => {
          return column.field === firstVisibleColumnName;
        });

        columnDefs[firstColumnIndex].cellRenderer = null;
        columnDefs[firstColumnIndex].cellRendererFramework = CellRenderer;
        columnDefs[firstColumnIndex].cellRendererParams = {
          type: type,
          field: columnDefs[firstColumnIndex].field,
        };
      }
      return columnDefs;
    },
    onModuleShowChange() {
      let type = "module";
      this.$store.commit("brain/ui/minimizeInput", {
        type,
        minimize: true,
        save: true,
      });
      this.changePaneSize(type, true);
      this.changeVisibiltyOfSplitter();
    },
    onDriverShowChange() {
      let type = "driver";
      this.$store.commit("brain/ui/minimizeInput", {
        type,
        minimize: true,
        save: true,
      });
      this.changePaneSize(type, true);
      this.changeVisibiltyOfSplitter();
    },
    changeVisibiltyOfSplitter() {
      let paneSize = this.$store.getters["brain/ui/paneSize"];
      let splitter = document.querySelector("div.splitpanes__splitter");
      if (splitter !== null && splitter !== undefined) {
        if (paneSize === 0.1 || paneSize === 99.9) {
          splitter.classList.add("splitter-hidden");
        } else if (
          splitter.classList.contains("splitter-hidden") &&
          paneSize === 50
        ) {
          splitter.classList.remove("splitter-hidden");
        }
      }
    },
    changePaneSize(type, hide) {
      let size = 0;
      let half = false;
      let hideLeft = this.$store.getters["brain/ui/minimizeModuleInput"];
      let hideRight = this.$store.getters["brain/ui/minimizeDriverInput"];

      //default values for type module
      let shown = 99.9;
      let hidden = 0;

      //default values for type driver
      if (type === "driver") {
        shown = 0;
        hidden = 99.9;
      }

      size = shown;
      if (hide) {
        size = hidden;
      } else {
        if (
          (!hideLeft && type === "driver") ||
          (!hideRight && type === "module")
        ) {
          half = true;
        }
        if (half) {
          size = 50;
        }
      }
      if (hideLeft && hideRight) {
        //do nothing
      } else {
        this.$store.commit("brain/ui/setPaneSize", {
          size: size,
          save: true,
        });
      }
    },
    async onMaximize(type) {
      await this.$store.commit("brain/ui/minimizeInput", {
        type,
        minimize: false,
        save: true,
      });
      this.changePaneSize(type, false);
      this.changeVisibiltyOfSplitter();
      this.calculate(type === "driver");
    },
    onSearchInputChange(e, component) {
      //e = searchTerm
      if (component === "module") {
        //commit searchTerm to module store
        this.$store.commit("brain/modules/setModuleSearchTerm", e.target.value);
        this.onModuleFilterChange();
      } else {
        //commit searchTerm to driver store
        this.$store.commit("brain/drivers/setDriverSearchTerm", e.target.value);
        this.onDriverFilterChange();
      }
    },
    onColumnVisibilityChange(e, type) {
      if (type === "module") {
        this.$store.commit("brain/ui/setModuleColumnVisibility", {
          value: e,
          save: true,
        });
      } else if (type === "multi-length") {
        this.$store.commit("brain/ui/setMultiLengthColumnVisibility", {
          value: e,
          save: true,
        });
      } else {
        this.$store.commit("brain/ui/setDriverColumnVisibility", {
          value: e,
          save: true,
        });
      }
    },
    resetRangeFilterGroup(group) {
      const toReturn = cloneDeep(group);
      toReturn.filters.forEach((filter) => {
        const lowestValue = Math.min(
          ...filter.availableValues.map((i) => i.value)
        );
        const highestValue = Math.max(
          ...filter.availableValues.map((i) => i.value)
        );
        filter.from = lowestValue;
        filter.to = highestValue;
        filter.value = null;
      });
      return toReturn;
    },
    onClear(type, group) {
      if (type === "modules") {
        if (group.filters[0].type === "range") {
          //assumes every filter in group is of same kind
          this.$store.commit(
            "brain/modules/resetSpecificFilterGroup",
            this.resetRangeFilterGroup(group)
          );
          this.$store.dispatch("brain/modules/getModules").then((modules) => {
            this.modules = modules;
          });
        } else {
          this.onModuleFilterChange({
            name: group.filters[0].name,
            values: [],
          });
        }
      } else if (type === "drivers") {
        if (group.filters[0].type === "range") {
          //assumes every filter in group is of same kind
          this.$store.commit(
            "brain/drivers/resetSpecificFilterGroup",
            this.resetRangeFilterGroup(group)
          );
          this.$store.dispatch("brain/drivers/getDrivers").then((drivers) => {
            this.drivers = drivers;
          });
        } else {
          this.onDriverFilterChange({
            name: group.filters[0].name,
            values: [],
          });
        }
      }
    },

    onClearModuleFiltersClicked() {
      this.$store.dispatch("brain/modules/resetAllFilters").then((modules) => {
        this.modules = modules;
      });
      this.resetDefaultFilters();
      this.$store.commit("brain/modules/setModuleSearchTerm", null);
    },

    onClearDriverFiltersClicked() {
      this.$store.dispatch("brain/drivers/resetAllFilters").then((drivers) => {
        this.drivers = drivers;
      });
      this.resetDefaultFilters();
      this.$store.commit("brain/drivers/setDriverSearchTerm", null);
    },

    resetDefaultFilters() {
      this.resetDefaultFilter("Has matching driver");
      this.resetDefaultFilter("Released combinations");
    },

    resetDefaultFilter(name) {
      let filter = { name: name };
      let isChecked = true;
      this.$store.commit("brain/global/updateFilter", { filter, isChecked });
    },

    showAllTags(type) {
      this.$modalRouter.push({
        name: "FilterListModal",
        props: { type: type },
        on: {
          moduleFilterChange: this.onModuleFilterChange,
          driverFilterChange: this.onDriverFilterChange,
          clearModuleFiltersClicked: this.onClearModuleFiltersClicked,
          clearDriverFiltersClicked: this.onClearDriverFiltersClicked,
        },
      });
    },

    async onModuleFilterChange(
      e = null,
      addToHistory = true,
      calcChange = false
    ) {
      const modules = await this.$store.dispatch(
        "brain/modules/handleFilterChange",
        {
          data: e,
          addToHistory,
        }
      );

      this.$store.commit("brain/modules/saveModuleCalculationParameters");

      if (this.solutionHasModule && !modules.length && calcChange) {
        this.$snotify.warning(
          "Please add a module to the solution in order to explore drivers",
          { position: "leftTop" }
        );
      }
      this.modules = modules;
      if (this.module && !this.modules.some((m) => m.uid === this.module.uid)) {
        this.$store.commit("brain/modules/setModule", null);
      }
    },
    async onDriverFilterChange(e = null, addToHistory = true) {
      this.drivers = await this.$store.dispatch(
        "brain/drivers/handleFilterChange",
        {
          data: e,
          addToHistory,
        }
      );

      this.$store.commit("brain/drivers/saveDriverCalculationParameters");

      if (this.driver && !this.drivers.some((d) => d.uid === this.driver.uid)) {
        this.$store.commit("brain/drivers/setDriver", null);
      }
    },

    onSelectModuleRow(selectedRow) {
      if (this.multiLength) {
        this.$store.commit(
          "brain/modules/setSelectedMultiLengthModule",
          selectedRow.data
        );
        this.$refs.moduleGraph.openContent();
      } else {
        this.$store.commit("brain/modules/setModule", selectedRow.data);
        this.$store.commit("brain/global/setActiveComponent", "module");
        this.$refs.moduleGraph.openContent();
      }
    },
    onSelectDriverRow(selectedRow) {
      this.$store.commit("brain/drivers/setDriver", selectedRow.data);
      this.$store.commit("brain/global/setActiveComponent", "driver");
      this.$refs.driverGraph.openContent();
    },
    //Will get the selectedRow and type
    onComponentAddToSolution(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      this.setComponent({ type: type, data: selectedRow, addNew: false });
    },
    //Will get the selectedRow and type
    onComponentAddToNewSolution(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      this.setComponent({ type: type, data: selectedRow, addNew: true }).then(
        () => {
          if (this.showTour) {
            // Adding an element to a new solution can cause an incorrect overlay when doing the tour.
            this.tour.refresh();
          }
        }
      );
    },
    onComponentAddToComparison(selectedRow, type) {
      if (!this.canI("COMPARE")) {
        this.$snotify.error(
          "You do not have sufficient permissions to compare products"
        );
      }

      this.$store.commit("brain/global/setActiveComponent", type);
      if (type === "module") {
        if (this.comparedModules.length === MAX_COMPARE_PRODUCTS) {
          this.$snotify.error(
            `The maximum amount of modules that can be compared is ${MAX_COMPARE_PRODUCTS}. Please remove one or more modules from the comparison and try again.`
          );
          return;
        }
        this.$store.commit("brain/modules/addToComparison", selectedRow.data);
      } else {
        if (this.comparedDrivers.length === MAX_COMPARE_PRODUCTS) {
          this.$snotify.error(
            `The maximum amount of drivers that can be compared is ${MAX_COMPARE_PRODUCTS}. Please remove one or more drivers from the comparison and try again.`
          );
          return;
        }
        this.$store.commit("brain/drivers/addToComparison", selectedRow.data);
      }
    },
    //Will get the selectedRow and type
    onComponentMoreInfo(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      this.$modalRouter.push({
        name: "ComponentDetailModal",
        on: {
          componentSelectedContinue: this.setComponent,
          onArrowKeyPress: this.selectComponent,
        },
        props: {
          multiLengthView: this.multiLength,
        },
      });
    },
    onComponentCopyToClipboard(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      const text = `${selectedRow.data.name}, ${selectedRow.data.nc12}`;
      this.copyToClipboard(text);
    },
    onComponentCopyLink(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      let settings = {};

      //To make sure we only send the selected row data; we want to fill the module data if a module is selected,
      //and otherwise (driver, emergencydriver, etc.) always fill the driver data.
      if (type === "module") {
        settings = {
          moduleFlux: this.$store.getters["brain/modules/moduleFlux"],
          moduleCurrent: this.$store.getters["brain/modules/moduleCurrent"],
          luminaireFlux: this.$store.getters["brain/modules/luminaireFlux"],
          lor: this.$store.getters["brain/modules/lor"],
          moduleTemperature:
            this.$store.getters["brain/modules/moduleTemperature"],
        };
      } else {
        settings = {
          driverCurrent: this.$store.getters["brain/drivers/driverCurrent"],
          driverVoltage: this.$store.getters["brain/drivers/driverVoltage"],
          driverPower: this.$store.getters["brain/drivers/driverPower"],
        };
      }

      const url = `${window.location.origin}/#/share?type=${type}&nc12=${
        selectedRow.data.nc12
      }&region=${this.region}&settings=${btoa(
        unescape(encodeURIComponent(JSON.stringify(settings)))
      )}`;
      this.copyToClipboard(url);
    },
    copyToClipboard(text) {
      const dummy = document.createElement("input");
      document.body.appendChild(dummy);
      dummy.value = text;
      dummy.select();
      document.execCommand("copy");
      document.body.removeChild(dummy);
    },
    selectComponent(props) {
      //Module table up and down functions
      if (props.type === "module") {
        if (props.direction === "up") {
          this.$refs.moduleTable.selectPreviousRow();
        } else {
          this.$refs.moduleTable.selectNextRow();
        }
      } else {
        //Driver table up and down functions
        if (props.direction === "up") {
          this.$refs.driverTable.selectPreviousRow();
        } else {
          this.$refs.driverTable.selectNextRow();
        }
      }
    },
    async setComponent({ type, data, addNew, removeAccessories = true }) {
      if (removeAccessories && this.hasSolutions && type === "driver") {
        this.onRemoveSensor(this.active);
        this.onRemoveBridge(this.active);
      }
      await this.$store.dispatch("brain/solutions/add", { type, addNew, data });

      if (this.multiLength) {
        await this.calculate(true);
      } else {
        await this.calculate();
      }

      this.checkIsWhitelistedSolution();
      if (!this.releasedSystemsOnly) {
        this.checkOutdoorIndoorCombination();
      }

      if (type === "module" && this.hasSolutions && this.solution.driver) {
        if (this.driver.uid === this.solution.driver.uid) {
          this.setComponent({
            type: "driver",
            data: this.driver,
            addNew: false,
            removeAccessories: false,
          });
        } else {
          this.onRemoveDriver(this.active);
        }
      }
    },

    checkIsWhitelistedSolution() {
      if (this.solutionDriver !== null && this.solutionModule !== null) {
        this.$store.dispatch("brain/drivers/fetchIsWhitelistedSolution");
      }
    },

    checkOutdoorIndoorCombination() {
      if (this.solutionDriver !== null && this.solutionModule !== null) {
        this.$store
          .dispatch("brain/drivers/fetchIsOddCombination")
          .then((isOddCombination) => {
            if (isOddCombination) {
              this.$modalRouter.push({
                name: "OutdoorDriverNoticeModal",
                props: { show: true },
                model: "show",
              });
            }
          });
      }
    },

    onRemoveModule(index) {
      this.$store.dispatch("brain/solutions/removeModule", index);
      this.calculate();
    },

    onRemoveDriver(index) {
      this.onRemoveSensor(index);
      this.onRemoveBridge(index);
      this.$store.dispatch("brain/solutions/removeDriver", index);
      this.calculate(this.multiLength);
    },

    onRemoveSensor(index) {
      if (this.solution) {
        this.$store.commit("brain/solutions/removeSensor", index);
      }
    },

    onRemoveBridge(index) {
      if (this.solution) {
        this.$store.commit("brain/solutions/removeBridge", index);
      }
    },

    onRemoveEmergencyDriver(index) {
      if (this.solution) {
        this.$store.dispatch("brain/solutions/removeEmergencyDriver", index);
      }
    },

    onClickModule() {
      if (this.minimizeModuleInput) {
        this.onMaximize("module");
      }
    },

    onClickDriver() {
      if (this.minimizeDriverInput) {
        this.onMaximize("driver");
      }
    },

    onSelectSolution(index) {
      this.$store
        .dispatch("brain/solutions/handleActiveSolution", index)
        .then((res) => {
          if (res === "multiLength") {
            this.modules = this.multiLengthModules;
            this.calculate(true);
          } else {
            this.calculate();
          }
        });
    },

    onViewSolution() {
      this.$modalRouter.push({
        name: "SolutionModal",
        props: {
          solution: this.solution,
        },
      });
    },

    onPaneResized(e) {
      this.$store.commit("brain/ui/setPaneSize", {
        size: e[0].width,
        save: true,
      });
    },

    calculate(driverOnly) {
      this.$store.commit("brain/global/setBusy", true);
      if (this.checkReload) {
        this.$store.commit("brain/global/setCheckReload", false);
        if (
          this.storeModules.length > 0 ||
          this.storeDrivers.length > 0 ||
          this.multiLength
        ) {
          if (this.multiLength) {
            if (
              this.multiLengthModules !== null &&
              this.multiLengthModules.length
            ) {
              this.modules = this.multiLengthModules;
            }
          } else {
            if (this.storeModules.length > 0) {
              this.modules = this.storeModules;
            } else {
              this.$store
                .dispatch("brain/modules/getModules")
                .then((modules) => {
                  this.modules = modules;
                });
            }
          }
          if (this.storeDrivers.length > 0) {
            this.drivers = this.storeDrivers;
          } else {
            this.$store.dispatch("brain/drivers/getDrivers").then((drivers) => {
              this.drivers = drivers;
            });
          }
          this.$store.commit("brain/global/setBusy", false, { root: true });
        }
      }

      if (!driverOnly && !this.minimizeModuleInput) {
        this.$store.dispatch("brain/modules/getModules").then((modules) => {
          this.modules = modules;

          // Restore incompatible module if it is compatible again
          if (
            !this.module &&
            this.incompatibleModule &&
            this.modules.some((m) => m.uid === this.incompatibleModule.uid)
          ) {
            this.$store.commit(
              "brain/modules/setModule",
              this.incompatibleModule
            );
            this.$store.commit("brain/modules/setIncompatibleModule", null);
          }
        });
      }
      if (this.multiLength && !this.minimizeModuleInput) {
        if (
          this.multiLengthModules !== null &&
          this.multiLengthModules.length
        ) {
          this.modules = this.multiLengthModules;
        }
      }

      if (!this.minimizeDriverInput) {
        this.$store.dispatch("brain/drivers/getDrivers").then((drivers) => {
          this.drivers = drivers;
        });
      }
      this.handleResize();
      this.$store.commit("brain/global/setBusy", false, { root: true });
    },

    addNewSolution() {
      this.$store.dispatch("brain/solutions/handleNewSolution", true);
      this.calculate();
    },

    removeSolution(index) {
      this.$store.commit("brain/solutions/removeSolution", index);
      if (this.solutions.length === 0) {
        {
          this.calculate();
        }
      }
    },

    moduleOptionBarAction(option) {
      if (
        this.moduleActiveTableOptionTab !== option.title &&
        option.title !== "Download"
      ) {
        this.moduleActiveTableOptionTab = option.title;
      } else {
        this.moduleActiveTableOptionTab = null;
      }
      if (option.title === "Download") {
        if (!this.isLoggedIn) {
          this.$snotify.error(
            "Downloading modules is only available for logged in users"
          );
        } else {
          this.$store.dispatch("brain/modules/downloadModules");
        }
      }
    },

    driverOptionBarAction(option) {
      if (
        this.driverActiveTableOptionTab !== option.title &&
        option.title !== "Download"
      ) {
        this.driverActiveTableOptionTab = option.title;
      } else {
        this.driverActiveTableOptionTab = null;
      }
      if (option.title === "Download") {
        if (!this.isLoggedIn) {
          this.$snotify.error(
            "Downloading drivers is only available for logged in users"
          );
        } else {
          this.$store.dispatch("brain/drivers/downloadDrivers");
        }
      }
    },
    startTour() {
      this.tour = new Tour({
        //https://kamranahmed.info/driver.js/
        ...tourConfig,
      });
      this.tour.defineSteps(tourSteps);
      this.tour.start();
    },
    unpinModule(module) {
      this.$store.dispatch("brain/global/deletePinnedProduct", {
        type: "module",
        uid: module.uid,
      });
    },
    unpinDriver(driver) {
      this.$store.dispatch("brain/global/deletePinnedProduct", {
        type: "driver",
        uid: driver.uid,
      });
    },
    addPinnedModuleToSolution(module) {
      this.$store.commit("brain/modules/setModule", module);
      this.setComponent({ type: "module", addNew: true, data: module });
    },
    addPinnedDriverToSolution(driver) {
      this.$store.commit("brain/drivers/setDriver", driver);
      this.setComponent({ type: "driver", addNew: true, data: driver });
    },
    onCompareModules() {
      this.$modalRouter.push({
        name: "CompareItemsModal",
        props: {
          show: true,
          type: "module",
          items: this.comparedModules,
          headers: this.compareModuleHeaders,
        },
        model: "show",
        on: {
          addToSolution: (module) =>
            this.onComponentAddToSolution(module, "module"),
        },
      });
    },

    onCompareDrivers() {
      this.$modalRouter.push({
        name: "CompareItemsModal",
        props: {
          show: true,
          type: "driver",
          items: this.comparedDrivers,
          headers: this.compareDriverHeaders,
        },
        modal: "show",
        on: {
          addToSolution: (driver) =>
            this.onComponentAddToSolution(driver, "driver"),
        },
      });
    },

    onRemoveComparedModule(module) {
      this.$store.commit("brain/modules/removeFromComparison", module);
    },

    onRemoveComparedDriver(driver) {
      this.$store.commit("brain/drivers/removeFromComparison", driver);
    },

    onClearComparedModules() {
      this.$store.commit("brain/modules/setComparedModules", []);
    },

    onClearComparedDrivers() {
      this.$store.commit("brain/drivers/setComparedDrivers", []);
    },

    handleResize() {
      let offset = 0;
      if (this.solutions.length > 0) {
        offset += 75;
      }
      if (this.$refs.modulePanel !== undefined) {
        const actionBarHeight = this.$parent.$refs.actionBar.$el.clientHeight;
        this.$refs.moduleScroll.$el.style.height =
          window.innerHeight - actionBarHeight - 5 - offset + "px";
      }

      if (this.$refs.driverPanel !== undefined) {
        const actionBarHeight = this.$parent.$refs.actionBar.$el.clientHeight;
        this.$refs.driverScroll.$el.style.height =
          window.innerHeight - actionBarHeight - 5 - offset + "px";
      }
    },

    unsetModuleGraphSticky() {
      if (this.$refs.moduleGraph !== undefined) {
        const moduleGraphEl = this.$refs.moduleGraph.$el;
        if (moduleGraphEl != null) {
          moduleGraphEl.classList.remove("is-sticky");
        }
      }
    },

    setModuleGraphSticky() {
      if (
        this.$refs.modulePanel.$el.clientHeight <
        this.$refs.moduleScroll.$el.clientHeight
      ) {
        return this.unsetModuleGraphSticky();
      }
      if (
        this.$refs.moduleGraph !== undefined &&
        this.$refs.moduleGraphPlaceholder !== undefined
      ) {
        const moduleGraphEl = this.$refs.moduleGraph.$el;
        const moduleGraphPlaceholderEl = this.$refs.moduleGraphPlaceholder;
        const actionBarHeight = this.$parent.$refs.actionBar.$el.clientHeight;
        if (moduleGraphEl != null) {
          moduleGraphEl.classList.add("is-sticky");
          moduleGraphEl.style.top = actionBarHeight + "px";
          moduleGraphPlaceholderEl.style.height =
            moduleGraphEl.offsetHeight + actionBarHeight + "px";
        }
      }
    },

    unsetDriverGraphSticky() {
      if (this.$refs.driverGraph !== undefined) {
        const driverGraphEl = this.$refs.driverGraph.$el;
        if (driverGraphEl != null) {
          driverGraphEl.classList.remove("is-sticky");
        }
      }
    },

    setDriverGraphSticky() {
      if (
        this.$refs.driverPanel.$el.clientHeight <
        this.$refs.driverScroll.$el.clientHeight
      ) {
        return this.unsetDriverGraphSticky();
      }
      if (
        this.$refs.driverGraph !== undefined &&
        this.$refs.driverGraphPlaceholder !== undefined
      ) {
        const driverGraphEl = this.$refs.driverGraph.$el;
        const driverGraphPlaceholderEl = this.$refs.driverGraphPlaceholder;
        const actionBarHeight = this.$parent.$refs.actionBar.$el.clientHeight;
        if (driverGraphEl != null) {
          driverGraphEl.classList.add("is-sticky");
          driverGraphEl.style.top = actionBarHeight + "px";
          driverGraphPlaceholderEl.style.height =
            driverGraphEl.offsetHeight + actionBarHeight + "px";
        }
      }
    },
    onSaveFilters(description) {
      this.$store
        .dispatch("brain/global/saveAllFilters", description)
        .then((response) => {
          //eventbus event to let savedfilters know to reload the filters.
          EventBus.$emit("filtersSaved");
          this.$snotify.success("Filters saved successfully");
          console.log("savefilters response: ", response);
        });
    },

    async restart() {
      await this.$store.commit("brain/modules/setModule", null);
      await this.$store.commit("brain/modules/setIncompatibleModule", null);
      await this.$store.commit("brain/drivers/setDriver", null);

      await this.$store.dispatch("brain/drivers/resetFilters", null);
      await this.$store.dispatch("brain/modules/resetFilters", null);

      await this.$store.commit("brain/drivers/setDriverSearchTerm", null);
      await this.$store.commit("brain/modules/setModuleSearchTerm", null);
      await this.$store.commit(
        "brain/emergencyDrivers/setEmergencyDriverSearchTerm",
        null
      );

      await this.$store.commit("brain/global/resetGlobalFilters", null);

      await this.$store.dispatch("brain/modules/clearCalculatedPerformance");
      await this.$store.commit("brain/drivers/clearCalculatedPerformance");
      await this.$store.commit(
        "brain/emergencyDrivers/clearCalculatedPerformance"
      );

      if (this.solution) {
        await this.$store.commit(
          "brain/solutions/removeEmergencyDriver",
          this.active
        );
        await this.$store.commit("brain/solutions/setVirtualModule", null);
        await this.$store.commit("brain/solutions/setMultiLengthModules", []);
        await this.$store.commit("brain/solutions/setMultiLength", false);
        await this.$store.commit("brain/solutions/setModuleFilters", null);
        await this.$store.dispatch("brain/solutions/removeModule", this.active);
        await this.$store.commit("brain/solutions/removeSensor", this.active);
        await this.$store.commit("brain/solutions/removeBridge", this.active);
        await this.$store.dispatch("brain/solutions/removeDriver", this.active);
      }

      await this.$store.commit("brain/modules/setComparedModules", []);
      await this.$store.commit("brain/drivers/setComparedDrivers", []);

      this.calculate();
    },

    handleSearchItem() {
      if (this.searchItem.nc12 !== null) {
        const type = this.searchItem.type.toLowerCase();

        this.$store
          .dispatch("brain/global/resetAllFiltersWithSearchTerm", {
            searchTerm: this.searchItem.nc12,
            component: type,
          })
          .then((response) => {
            this.modules = response.modules;
            this.drivers = response.drivers;
            if (this[`${type}s`].length === 0 && this.getModal !== null) {
              this.getModal.showModal();
            }
          });

        switch (type) {
          case "module":
            this.onDriverShowChange();
            break;

          case "driver":
            this.onModuleShowChange();
            break;
        }

        const settings = this.searchItem.settings;
        if (settings) {
          switch (type) {
            case "module":
              this.$store.commit(
                "brain/modules/setModuleFlux",
                settings.moduleFlux
              );
              this.$store.commit(
                "brain/modules/setModuleCurrent",
                settings.moduleCurrent
              );
              this.$store.commit(
                "brain/modules/setLuminaireFlux",
                settings.luminaireFlux
              );
              this.$store.commit("brain/modules/setLor", settings.lor);
              this.$store.commit(
                "brain/modules/setModuleTemperature",
                settings.moduleTemperature
              );
              break;
            case "driver":
              this.$store.commit(
                "brain/drivers/setDriverCurrent",
                settings.driverCurrent
              );
              this.$store.commit(
                "brain/drivers/setDriverVoltage",
                settings.driverVoltage
              );
              this.$store.commit(
                "brain/drivers/setDriverPower",
                settings.driverPower
              );
              break;
          }
        }
      }
    },
  },

  mounted() {
    if (this.showTour && this.baseLayoutMounted) {
      this.startTour();
    } else if (this.showTour) {
      this.unwatchBaseLayoutMounted = this.$watch(
        "baseLayoutMounted",
        (newVal) => {
          if (newVal === true) {
            this.startTour();
            this.unwatchBaseLayoutMounted();
          }
        }
      );
    }
    this.changeVisibiltyOfSplitter();
    this.handleResize();
  },
  watch: {
    showTour(newValue) {
      if (newValue === true) {
        this.startTour();
      }
    },
    resultDriverColumnSet() {
      this.getDriverColumnDefs();
    },
    resultModuleColumnSet() {
      if (this.multiLength) {
        this.getMultiLengthColumnDefs();
      } else {
        this.getModuleColumnDefs();
      }
    },
    resultMultiLengthColumnSet() {
      this.getMultiLengthColumnDefs();
    },
    searchItem() {
      // Remove the query parameters without reloading the page
      window.history.replaceState(
        {},
        document.title,
        document.location.href.split("?")[0]
      );
      this.handleSearchItem();
    },
  },
  created() {
    this.$store.dispatch("brain/modules/canRTH");
    this.getDriverColumnDefs();
    this.getModuleColumnDefs();
    this.getMultiLengthColumnDefs();
    EventBus.$on(
      "brain/modules/handleFilterChange",
      ({
        data = null,
        addToHistory = true,
        calcChange = false,
        done = () => {},
      }) => {
        this.onModuleFilterChange(data, addToHistory, calcChange).then(done);
      }
    );

    EventBus.$on(
      "brain/drivers/handleFilterChange",
      ({ data = null, addToHistory = true, done = () => {} }) => {
        this.onDriverFilterChange(data, addToHistory).then(done);
      }
    );

    EventBus.$on("recalculate", (page) => {
      if (page === "advanced") {
        this.calculate();
      }
    });

    EventBus.$on("restart", () => {
      this.restart();
    });

    EventBus.$on("recalculateDrivers", () => {
      this.calculate(true);
    });

    EventBus.$on("setMultiLength", () => {
      this.$store.commit("brain/modules/setModule", this.virtualModule);
      this.setComponent({
        type: "module",
        addNew: false,
        data: this.virtualModule,
      });
    });

    this.$store.commit("brain/ui/showControls", true);

    // get pinned products
    if (this.isLoggedIn) {
      this.$store.dispatch("brain/global/getPinnedProducts");
    }

    // Check if query parameters are set
    if (this.$route.query.driver) {
      //Check if a new solution has to be made
      let newSolution = true;
      const solutionsString = localStorage.getItem("solutions");
      if (solutionsString && JSON.parse(solutionsString).solutions.length) {
        const solutions = JSON.parse(solutionsString);
        const activeSolution = solutions.solutions[solutions.active];
        if (
          activeSolution &&
          activeSolution.driver &&
          activeSolution.driver.nc12 === this.$route.query.driver
        ) {
          newSolution = false;
        }
      }

      if (this.$route.query.bridge) {
        // Check bridge
        this.$store
          .dispatch("brain/solutions/setBridgeFromUrl", {
            bridgeNc12: this.$route.query.bridge,
          })
          .then((res) => {
            if (newSolution) {
              // Add new solution
              this.$store.dispatch("brain/solutions/handleNewSolution", false, {
                root: true,
              });
            }

            //Add bridge
            this.$store.commit("brain/solutions/setBridge", res);

            // Add other products
            if (this.$route.query.sensor) {
              // Add sensor
              this.$store.dispatch("brain/solutions/setSensorFromUrl", {
                sensorNc12: this.$route.query.sensor,
                accessoryIds:
                  this.$route.query.accessories != null
                    ? this.$route.query.accessories
                    : null,
              });
            }
            if (newSolution) {
              // Add Driver
              this.$store
                .dispatch("brain/solutions/setDriverFromUrl", {
                  driverNc12: this.$route.query.driver,
                })
                .then(() => {
                  this.calculate();
                });
            } else {
              this.calculate();
            }
          })
          .catch((err) => {
            this.calculate();
            console.log("Invalid bridge:", err);
          });
      } else {
        // Check if driver and sensor are whitelisted to eachother
        if (this.$route.query.sensor) {
          this.$store
            .dispatch("brain/drivers/checkSensorWhitelist", {
              sensorNc12: this.$route.query.sensor,
              driverNc12: this.$route.query.driver,
            })
            .then((res) => {
              if (res.data.status === "success") {
                if (newSolution) {
                  // Add new solution
                  this.$store.dispatch(
                    "brain/solutions/handleNewSolution",
                    false,
                    {
                      root: true,
                    }
                  );
                }
                // Set sensor from query parameter
                this.$store
                  .dispatch("brain/solutions/setSensorFromUrl", {
                    sensorNc12: this.$route.query.sensor,
                    accessoryIds:
                      this.$route.query.accessories != null
                        ? this.$route.query.accessories
                        : null,
                  })
                  .then(() => {
                    if (newSolution) {
                      // Set driver from query parameter
                      this.$store
                        .dispatch("brain/solutions/setDriverFromUrl", {
                          driverNc12: this.$route.query.driver,
                        })
                        .then(() => {
                          this.calculate();
                        });
                    } else {
                      this.calculate();
                    }
                  })
                  .catch((err) => {
                    this.calculate();
                    console.log(err);
                  });
              }
            })
            .catch((err) => {
              this.calculate();
              console.log(err);
            });
        } else {
          if (newSolution) {
            // Set driver from query parameter
            this.$store
              .dispatch("brain/solutions/handleNewSolution", false, {
                root: true,
              })
              .then(() => {
                this.$store
                  .dispatch("brain/solutions/setDriverFromUrl", {
                    driverNc12: this.$route.query.driver,
                  })
                  .then(() => {
                    this.calculate();
                  });
              });
          } else {
            this.calculate();
          }
        }
      }
    } else if (
      this.isLoggedIn &&
      this.fetchFiltersOnInit &&
      this.fetchViewOnInit &&
      !this.isImpersonating &&
      !this.isAdministering
    ) {
      this.$store
        .dispatch("brain/global/fetchLatestFilter")
        .then((response) => {
          if (response.data.data !== null) {
            const decodedFilters = JSON.parse(response.data.data.filters);
            this.$store.commit(
              "brain/global/setFilter",
              decodedFilters.globalFilters
            );
            this.$store.dispatch(
              "brain/modules/setFilters",
              decodedFilters.modulesFilters
            );
            this.$store.dispatch(
              "brain/drivers/setFilters",
              decodedFilters.driversFilters
            );
          }
          this.calculate(this.multiLength);
        })
        .catch((err) => {
          console.log(err.response);
          this.calculate(this.multiLength);
        });
      this.$store
        .dispatch("brain/global/fetchLatestDisplaySettings")
        .then((response) => {
          if (response.data.data !== null) {
            const decodedDisplaySettings = JSON.parse(response.data.data.view);
            this.$store.commit("brain/ui/setPaneSize", {
              size: decodedDisplaySettings.paneSize,
            });
            this.$store.commit("brain/ui/minimizeInput", {
              type: "module",
              minimize: decodedDisplaySettings.minimizeModuleInput,
            });
            this.$store.commit("brain/ui/minimizeInput", {
              type: "driver",
              minimize: decodedDisplaySettings.minimizeDriverInput,
            });

            decodedDisplaySettings.savedModuleColumnVisibility.map((column) => {
              let columnObject = { item: column, value: column.value };
              this.$store.commit("brain/ui/setModuleColumnVisibility", {
                value: columnObject,
              });
            });

            decodedDisplaySettings.savedDriverColumnVisibility.map((column) => {
              let columnObject = { item: column, value: column.value };
              this.$store.commit("brain/ui/setDriverColumnVisibility", {
                value: columnObject,
              });
            });
          }
        })
        .catch((err) => {
          console.log(err);
        });
    } else if (!this.searchItem) {
      this.calculate(this.multiLength);
    }

    this.handleSearchItem();

    // Module graph fixed top after scroll
    window.addEventListener("resize", this.handleResize);

    if (this.openSolutionModal) {
      this.onViewSolution();
    }
  },
  destroy() {
    this.$store.commit("brain/ui/showControls", false);
    if (this.unwatchBaseLayoutMounted) {
      this.unwatchBaseLayoutMounted();
    }
  },

  beforeDestroy() {
    EventBus.$off("brain/modules/handleFilterChange");
    EventBus.$off("brain/drivers/handleFilterChange");
    EventBus.$off("restart");
  },
};
</script>

<style lang="scss">
.advanced-view {
  display: flex;
  flex-direction: column;
  flex-grow: 2;

  .panels {
    display: flex;
    flex-grow: 2;
    justify-content: space-between;
    align-items: stretch;
  }
  .easy-minimize {
    .content {
      flex-direction: column;
    }
  }

  .splitpanes {
    height: auto;
  }

  .splitter-hidden {
    display: none;
  }

  .scroll-area {
    position: relative;
    margin: 0;
    width: 100%;

    &.left {
      border-right: 1px solid $mid-gray-200;
    }

    &.right {
      border-left: 1px solid $mid-gray-200;
    }
  }

  .driver,
  .module {
    justify-content: space-between;

    .scroll-window {
      position: relative;
      height: 100%;

      //safari only css for responsiveness fix
      @media not all and (min-resolution: 0.001dpcm) {
        @supports (-webkit-appearance: none) {
          max-width: 272px;
        }
      }
    }

    .content {
      height: auto;
      flex-grow: 2;
      justify-content: space-between;
    }
  }

  .driver {
    justify-content: flex-end;
  }

  .results-block {
    display: flex;
    height: 100%;

    &.hide-module-cta {
      .module-table {
        .cta {
          display: none;
        }
      }
    }

    &.hide-driver-cta {
      .driver-table {
        .cta {
          display: none;
        }
      }
    }

    .driver-table,
    .module-table {
      flex-grow: 2;
      display: flex;
      flex-direction: column;
      width: 100%;

      .easy-table-options-bar {
        height: 40px;
      }

      .easy-table {
        .ag-theme-balham {
          .ag-cell {
            line-height: 32px;

            .pin-icon {
              margin-top: -4px;
            }
          }

          .ag-tooltip {
            z-index: 9999;
          }
        }
      }

      /*
      $ctaSize: 24px;
      .cta {
        width: $ctaSize;
        height: $ctaSize;
        border-radius: $ctaSize;
        line-height: $ctaSize;

        &.cta-add {
          left: -30px !important;
        }

        svg {
          width: $ctaSize;
          height: $ctaSize;
        }
      }
      */

      .phase-out {
        color: #e98500;
      }

      .phase-in {
        color: #00bfff;
      }

      .pre-cr {
        color: #32cd32;
        white-space: nowrap;
      }

      .is-archived {
        color: #a900ff;
        white-space: nowrap;
      }
    }
  }
  .default-theme.splitpanes--vertical > .splitpanes__splitter {
    z-index: map-get($z-index, splitpanes-splitter);
    border-right: 1px solid #eee;
    width: 10px;
    margin-left: 0;

    &:hover {
      background-color: $dark-gray-900;
    }
  }

  .legend-padding {
    padding-left: 1rem;
    padding-top: 0.5rem;
    position: relative;
    margin: 0;
  }

  .no-graph-notice {
    padding-left: 1.5rem;
  }

  .multi-length-accordion {
    padding: 0 1.5rem;
  }

  .accordion-media {
    align-items: flex-start;
    display: flex;
    padding: 0 1.5rem;

    img {
      width: 75px;
      height: 75px;
    }

    .accordion-media-body {
      align-self: center;
      flex: 1;
    }
  }
}

#module-graph-placeholder,
#driver-graph-placeholder {
  display: none;
  padding: 1.11rem 0;
}

#module-graph.open.is-sticky,
#driver-graph.open.is-sticky {
  background-color: $white;
  box-shadow: -10px 2px 14px 0px rgba(0, 0, 0, 0.25);
  left: 0;
  position: fixed;
  top: 0;
  width: 270px;
  z-index: map-get($z-index, sticky-chart);

  .legend {
    display: none;
  }

  + #module-graph-placeholder {
    display: block;
  }

  + #driver-graph-placeholder {
    display: block;
  }
}
#driver-graph.open.is-sticky {
  box-shadow: 10px 2px 14px 0px rgba(0, 0, 0, 0.25);
  left: auto;
  right: 0;
}

.search-container {
  padding: 0 1.5rem;
}

#modal {
  height: 25%;
  width: 25%;
  z-index: 100;
  background: #ffff;
  border: 1px solid $green;
  border-radius: 3px;
}

.modal-main {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.legend {
  .half-container {
    ul {
      display: flex;
      flex-direction: column;
    }
    padding: 0;
  }
  ul {
    list-style: none;
    padding-left: 1rem;
    li {
      margin: 10px 10px 0 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      .legend-label {
        margin-left: 3px;
        font-size: $font-size-legend;
      }
    }
    span {
      &.red {
        background-color: $red;
        color: $red;
      }
      &.blue {
        background-color: $blue;
        color: $blue;
      }
      &.green {
        background-color: $green;
        color: $green;
      }
      &.orange {
        background-color: $orange;
        color: $orange;
      }
      &.black {
        background-color: $black;
        color: $black;
      }

      &.line {
        float: left;
        width: 18px;
        height: 2px;
        border-width: 1px;
        border-style: solid;
      }
      &.dot {
        float: left;
        width: 10px;
        height: 10px;
        margin: 0 4px;
        border-radius: 50%;
        border-width: 5px;
        border-style: solid;
      }
      &.dotted {
        float: left;
        width: 18px;
        height: 0;
        border-width: 1px;
        border-color: currentColor;
        background-color: white;
        border-style: dashed;
      }
    }
  }
  padding-top: 10px;
}
</style>
